// The reader (HomeMail): the actions in the core's top line in groups — sort out | mark | answer; under it the letter is a single page like a record's
// (um/2026-10-30/03 п.8): the subject as the title, the letter's properties in the record's list, the chain with earlier letters
// folded, and the CRM records the chain is about as a block of related records — not a column beside it. Empty — the keys to remember.

import { useEffect, useMemo, useState } from 'react';
import type { Id } from '@confdeck/core-shared';
import { Icon } from '../../ui/icons.tsx';
import { IconButton, Button, cx } from '../../ui/primitives.tsx';
import { TopBar } from '../../ui/TopBar.tsx';
import type { ReplyMode } from './drafts.ts';
import { HtmlFrame } from './HtmlFrame.tsx';
import { Linked } from './Linked.tsx';
import { InfoList, SingleTitle } from '../../record/single.tsx';
import { singleClass } from '../../ui/Page.tsx';
import { FOLDER_LIST, bodyOf, fileSize, fullTime, inView, people, type Body, type Letter } from './model.ts';
import { Path, type Crumb } from '../../record/RecordPath.tsx';
import { useMailNav } from './nav-state.ts';
import { useMailboxes } from './use-letters.ts';
import { useSync } from '../../sync/react.tsx';
import { SHORTCUTS } from './shortcuts.ts';
import s from './mail.module.css';

export function Reader({ letter, all, help, onReply, onMove, onMark }: {
  letter: Letter | null; all: Letter[]; help: boolean;
  onReply(l: Letter, mode: ReplyMode): void; onMove(ids: Id[], folder: 'inbox' | 'archive' | 'trash' | 'junk'): void;
  onMark(ids: Id[], c: { unread?: boolean; starred?: boolean }): void;
}) {
  const thread = useMemo(() => (letter ? all.filter((l) => l.thread === letter.thread && l.folder !== 'drafts' && (l.folder !== 'trash' || letter.folder === 'trash')).sort((a, b) => (a.date < b.date ? -1 : 1)) : []), [all, letter]);
  const linked = useMemo(() => [...new Set(thread.flatMap((l) => l.linked))], [thread]);
  const crumbs = useLetterCrumbs(letter);

  if (!letter || help) {
    const keys = help ? SHORTCUTS : SHORTCUTS.filter(([k]) => ['↓ / J', 'E', 'R', 'C', '/', '?'].includes(k));
    return (
      <>
        <TopBar />
        <div className={s.idle}>
          <Icon name="mail" size={32} />
          <span>{help ? 'Клавиши почты' : 'Выберите письмо'}</span>
          <dl className={s.keys}>{keys.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        </div>
      </>
    );
  }
  const ids = [letter.id];
  const binned = letter.folder === 'trash' || letter.folder === 'junk';
  const last = thread.at(-1) ?? letter;
  return (
    <>
      <TopBar label="Действия с письмом" start={<Path crumbs={crumbs} label="Путь к письму" />} end={<>
        <div className={s.group} role="group" aria-label="Разобрать">
          {binned
            ? <IconButton size="sm" icon="undo" label="Вернуть во входящие" hint="Вернуть во «Входящие»" onClick={() => onMove(ids, 'inbox')} />
            : <IconButton size="sm" icon="archive" label="В архив" hint="В архив (E)" onClick={() => onMove(ids, 'archive')} />}
          {letter.folder !== 'junk' && <IconButton size="sm" icon="junk" className={s.optional} label="В спам" hint="В спам (!)" onClick={() => onMove(ids, 'junk')} />}
          {letter.folder !== 'trash' && <IconButton size="sm" icon="trash" label="В корзину" hint="В корзину (#)" onClick={() => onMove(ids, 'trash')} />}
        </div>
        <div className={s.group} role="group" aria-label="Отметить">
          <IconButton size="sm" icon={letter.unread ? 'mailRead' : 'mailUnread'} label={letter.unread ? 'Отметить прочитанным' : 'Отметить непрочитанным'} hint={letter.unread ? 'Отметить прочитанным (U)' : 'Отметить непрочитанным (U)'} onClick={() => onMark(ids, { unread: !letter.unread })} />
          <IconButton size="sm" icon={letter.starred ? 'starFilled' : 'star'} pressed={letter.starred} className={letter.starred ? s.starOn : undefined} label={letter.starred ? 'Снять «Важное»' : 'Важное'} hint="Важное (S)" onClick={() => onMark(ids, { starred: !letter.starred })} />
        </div>
        <div className={s.group} role="group" aria-label="Ответить">
          <IconButton size="sm" icon="reply" label="Ответить" hint="Ответить (R)" onClick={() => onReply(last, 'reply')} />
          <IconButton size="sm" icon="replyAll" className={s.optional} label="Ответить всем" hint="Ответить всем (A)" onClick={() => onReply(last, 'replyAll')} />
          <IconButton size="sm" icon="forward" className={s.optional} label="Переслать" hint="Переслать (F)" onClick={() => onReply(last, 'forward')} />
        </div>
      </>} />
      <div className={s.readerScroll} key={letter.thread}>
        <div className={singleClass}>
          <SingleTitle level={2} after={thread.length > 1 ? <span className={s.threadCount}>{thread.length}</span> : undefined}>{letter.subject || '(без темы)'}</SingleTitle>
          <InfoList rows={letterProps(letter)} />
          <section className={s.chain} aria-label="Цепочка">
            {thread.map((l) => <ThreadLetter key={l.id} letter={l} initiallyOpen={l.id === letter.id || l.id === last.id || l.unread} />)}
          </section>
          <div className={s.replyBar}>
            <Button variant="secondary" icon="reply" onClick={() => onReply(last, 'reply')}>Ответить</Button>
            <Button variant="secondary" icon="replyAll" onClick={() => onReply(last, 'replyAll')}>Ответить всем</Button>
            <Button variant="secondary" icon="forward" onClick={() => onReply(last, 'forward')}>Переслать</Button>
          </div>
          {linked.length > 0 && <Linked ids={linked} />}
        </div>
      </div>
    </>
  );
}

function ThreadLetter({ letter: l, initiallyOpen }: { letter: Letter; initiallyOpen: boolean }) {
  const [open, setOpen] = useState(initiallyOpen);
  const [body, setBody] = useState<Body | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open || body) return;
    let alive = true;
    bodyOf(l.id).then((b) => { if (alive) setBody(b); }).catch((e: Error) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [open, l.id, body]);
  const name = l.fromName || l.from;
  const recipients = [...people(l.to), ...people(l.cc)].map((p) => p.name).join(', ');
  return (
    <article className={cx(s.letter, !open && s.letterFolded)}>
      <button type="button" className={s.letterHead} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className={s.avatar} aria-hidden>{initials(name)}</span>
        <span className={s.letterWho}>
          <span className={s.letterFrom}>{name}{l.fromName && <span className={s.letterAddr}>{l.from}</span>}</span>
          <span className={s.letterTo}>{open ? `Кому: ${recipients || '—'}` : l.snippet}</span>
        </span>
        {l.attachments.length > 0 && !open && <Icon name="paperclip" size={14} />}
        <time className={s.letterTime} dateTime={l.date}>{fullTime(l.date)}</time>
      </button>
      {open && (
        <div className={s.letterBody}>
          {l.status && l.error && <div className={s.notice} data-tone="danger"><Icon name="junk" size={14} />Письмо не ушло: {l.error}</div>}
          {l.attachments.length > 0 && (
            <ul className={s.files} aria-label="Вложения">
              {l.attachments.map((a) => (
                <li key={a.url || a.name}>
                  {a.url
                    ? <a className={s.file} href={a.url} download={a.name}><Icon name="file" size={14} /><span className={s.fileName}>{a.name}</span><span className={s.fileSize}>{fileSize(a.size)}</span></a>
                    : <span className={s.file} title="Файл не сохранён: хранилище файлов не подключено"><Icon name="file" size={14} /><span className={s.fileName}>{a.name}</span><span className={s.fileSize}>не сохранён</span></span>}
                </li>
              ))}
            </ul>
          )}
          {error ? <div className={s.notice} data-tone="danger">{error}</div>
            : !body ? <div className={s.skeleton} aria-hidden />
            : body.html ? <HtmlFrame html={body.html} /> : <div className={s.text}>{body.text || l.snippet}</div>}
        </div>
      )}
    </article>
  );
}

/** The properties of the opened letter, as a record's: who wrote, to whom, copies, when, the files. */
function letterProps(l: Letter) {
  const list = (v: string) => people(v).map((p) => (p.name !== p.address ? `${p.name} <${p.address}>` : p.address)).join(', ');
  return [
    { icon: 'user', label: 'От', value: l.fromName ? `${l.fromName} <${l.from}>` : l.from },
    { icon: 'users', label: 'Кому', value: list(l.to) || null },
    ...(l.cc ? [{ icon: 'users', label: 'Копия', value: list(l.cc) || null }] : []),
    { icon: 'date', label: 'Дата', value: fullTime(l.date) },
    ...(l.attachments.length ? [{ icon: 'paperclip', label: 'Вложения', value: `${l.attachments.length}` }] : []),
  ];
}

const initials = (name: string) => name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

/**
 * The crumbs of the open letter, as on a record's page (um/2026-10-31/02): the section › the mailbox, when one is chosen › the folder the
 * person is in (the letter's own one when it came from a search) › the subject, cut with «…».
 */
function useLetterCrumbs(letter: Letter | null): Crumb[] {
  const { box, view } = useMailNav();
  const boxes = useMailboxes();
  // The first crumb is the rail section, named as on the pages of templates and mailings («Письма»).
  const section = useSync().menu.find((m) => m.key === 'mail')?.label ?? 'Почта';
  if (!letter) return [];
  const folder = inView(letter, view) ? view : letter.folder;
  const name = box === 'all' ? null : boxes.find((b) => b.id === box)?.name;
  return [
    { label: section, href: '/s/mail' },
    ...(name ? [{ label: name }] : []),
    { label: FOLDER_LIST.find((f) => f.key === folder)?.label ?? 'Почта' },
    { label: letter.subject || '(без темы)', title: letter.subject },
  ];
}
